<template>
  <page-template>
    <template #title>Scales</template>
    <template #type>
      <app-drop-down-menu setting="type"
                          :options="types"
                          text="Type: "
                          :currently-selected="type"
                          @updated="setState"
                          :small="true"/>
    </template>
    <template #search>
      <app-search @search="searchScales" @termChange="termChange"/>
    </template>
    <template #content>
      <v-expansion-panels focusable v-if="pagination.data().length">
        <scale-card v-for="(data, index) in pagination.data()"
                    :key="data['p'] + index"
                    :data="data"
                    :index="index + (pagination.pageStart)"/>
      </v-expansion-panels>
      <v-col :cols="12" v-else class="display-1 blue--text">
        No Scales Found!
      </v-col>
    </template>
    <template #pagination>
      <v-pagination circle class="border" color="primary"
                    v-model="pagination.page"
                    :length="pagination.pages"/>
    </template>
    <template #help-title>Help</template>
    <template #help-text>
      Infront of you are multiple cards of scales, where each card represents a musical scale which you can play and
      click to get more information about, such as it's chords, 7<sup>th</sup> chords.
      <br>You can even click on the chords to see their inversions and play them too!
      <br><br>
      You can also change the scale type to generate different types of scales,
      or search for a specifc scale by name.
      <br><br>
      You can also press the 'Go to studio' button to navigate to scale heaven studio and compose using the scale you
      chose, creating interesting harmonies and chord progressions for your new song!
    </template>
  </page-template>
</template>

<script>
  import Paginator                                from 'vue-paginator3k'
  import classicScales                            from 'assets/json/common-scale-ref'
  import { setTransportBPM, setTransportLooping } from 'helpers/tone-helpers'
  import { getConstants }                         from 'helpers/note-art-helpers'

  let scales

  export default {
    name: 'Scales',

    head: {
      title: function() {
        return {
          inner: 'Scales',
        }
      },
      meta:  [
        {
          name:    'music theory scales',
          content: 'an interactive list of music scales to explore and play',
          id:      'desc',
        },
      ],
    },

    components: {
      PageTemplate:    () => import(/* webpackChunkName: "components" */ 'components/PageTemplate'),
      AppDropDownMenu: () => import(/* webpackChunkName: "components" */ 'components/AppDropDownMenu'),
      ScaleCard:       () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/cards/ScaleCard'),
      AppSearch:       () => import(/* webpackChunkName: "components" */ 'components/AppSearch'),
    },

    created() {
      setTransportBPM(120)
      setTransportLooping(false)
      this.generate()
    },

    data() {
      return {
        route:           'scales_and_chords',
        ready:           true,
        generatedScales: [],
        types:           [
          'Classic',
          'Heptatonic',
          'Hexatonic',
          'Octatonic',
          'Pentatonic',
          'Theoretical',
          'All',
        ],
        type:            'Classic',
        typeArray:       getConstants().ClassicScales,
        term:            null,
        pagination:      new Paginator(10),
      }
    },

    methods: {
      async generate() {
        this.generatedScales.length = 0
        this.typeArray              = await this.getChosenType()
        for(let s of this.typeArray) {
          this.generatedScales.push(s)
        }
        this.pagination.data(this.generatedScales)
      },

      async getChosenType() {
        if(this.type === 'Classic') {
          return classicScales
        }
        await getScales()
        switch(this.type) {
          case 'Heptatonic':
            return scales.filter(scale =>
              scale['l'] === '7' && scale['n'] !== 'Theoretical',
            )
          case 'Hexatonic':
            return scales.filter(scale =>
              scale['l'] === '6' && scale['n'] !== 'Theoretical',
            )
          case 'Octatonic':
            return scales.filter(scale =>
              scale['l'] === '8' && scale['n'] !== 'Theoretical',
            )
          case 'Pentatonic':
            return scales.filter(scale =>
              scale['l'] === '5' && scale['n'] !== 'Theoretical',
            )
          case 'Theoretical':
            return scales.filter(scale =>
              scale['n'] === 'T',
            )
          case 'All':
            return scales
          case 'Search':
            return await this.searchScales(this.term)
        }
      },

      async searchScales(term) {
        if(term) {
          this.type   = 'Search'
          const found = []
          const t     = term.toLowerCase()
          let text
          scales.forEach((s) => {
            text = s['n'].toLowerCase()
            if(s['a']) {
              text = text.concat(s['a'])
            }
            if(text.includes(t)) {
              found.push(s)
            }
          })
          return found
        }
      },

      termChange(term) {
        this.term = term
        if(this.type !== 'Search') {
          this.type = 'Search'
        }
        if(term) {
          this.generate()
        }
      },

      setState({ setting, value }) {
        this[setting] = value
        this.generate()
      },
    },
  }

  async function getScales() {
    if(!scales) {
      await fetch('/scales-ref.json')
        .then(res => res.json())
        .then(data => {
          scales = data
        })
    }
  }
</script>
